/**
 * bytes_cache — S8 (LOD-switch research, 2026-09-02): the fetched GLB bytes
 * of recently loaded levels, kept in a bounded LRU so a level the asset
 * cache evicted and a return to the city reloads parses FROM MEMORY instead
 * of the network (the store sends no Cache-Control, so a browser revalidates
 * or refetches). Pack members never come through here — the opened .vpak
 * already holds their bytes. Only bytes: the report's "decoded buffers" tier
 * is not possible while the engine strips CPU-side mip payloads after upload
 * (the F15 heap fix), so a disposed scene could not re-upload its textures.
 * Pure over ArrayBuffers so the harness pins the budget and the LRU order.
 */
export class BytesLru {
  private readonly map = new Map<string, { bytes: ArrayBuffer; at: number }>();
  private total = 0;
  private clock = 0;
  readonly stats = { hits: 0, misses: 0, puts: 0, evictions: 0 };
  constructor(readonly budgetBytes: number) {}
  get size(): number { return this.map.size; }
  get bytes(): number { return this.total; }
  get(key: string): ArrayBuffer | null {
    const e = this.map.get(key);
    if (!e) { this.stats.misses++; return null; }
    e.at = ++this.clock; this.stats.hits++;
    return e.bytes;
  }
  /** Store (or refresh) an entry; oversized entries are not kept; the oldest go when over budget. */
  put(key: string, bytes: ArrayBuffer): void {
    if (bytes.byteLength > this.budgetBytes) return;
    const old = this.map.get(key);
    if (old) { this.total -= old.bytes.byteLength; this.map.delete(key); }
    this.map.set(key, { bytes, at: ++this.clock });
    this.total += bytes.byteLength; this.stats.puts++;
    while (this.total > this.budgetBytes && this.map.size > 1) {
      let oldestKey: string | null = null, oldest = Infinity;
      for (const [k, e] of this.map) if (e.at < oldest) { oldest = e.at; oldestKey = k; }
      if (oldestKey === null) break;
      this.total -= this.map.get(oldestKey)!.bytes.byteLength;
      this.map.delete(oldestKey); this.stats.evictions++;
    }
  }
  clear(): void { this.map.clear(); this.total = 0; }
}
export const BYTES_CACHE_BUDGET = 96 * 1024 * 1024;
